import React, { useEffect, useState } from "react";
import { Mail, Bell, Loader2, Send, CheckCircle2, Flame } from "lucide-react";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import api from "../lib/api";
import { toast } from "sonner";

// HH:MM slots on the hour + half-hour (UTC). User picks; the backend
// scheduler polls every 5 min, so any 5-min slot would technically work but
// hour/half-hour keeps the UI simple.
function buildTimeOptions() {
  const out = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 30]) {
      out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return out;
}

const TIMES = buildTimeOptions();

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [prefs, setPrefs] = useState({ digest_enabled: false, digest_time: "13:00" });
  const [email, setEmail] = useState("");
  const [lastSent, setLastSent] = useState(null);

  // Streak push notification prefs — separate schedule from the daily digest
  // (digest is a morning email, streak push is an evening nudge by default).
  const [streakPrefs, setStreakPrefs] = useState({ streak_push_enabled: true, streak_push_time: "23:00" });
  const [streakSaving, setStreakSaving] = useState(false);
  const [streakSending, setStreakSending] = useState(false);
  const [streakLastSent, setStreakLastSent] = useState(null);

  // Load preferences once on mount. Subsequent saves call /settings/digest
  // PUT and locally update state — no need to re-fetch.
  useEffect(() => {
    Promise.all([
      api.get("/settings/digest").catch(() => null),
      api.get("/settings/streak-push").catch(() => null),
    ])
      .then(([d, s]) => {
        if (d) {
          setPrefs({ digest_enabled: d.data.digest_enabled, digest_time: d.data.digest_time });
          setEmail(d.data.email);
          setLastSent(d.data.digest_last_sent_at);
        }
        if (s) {
          setStreakPrefs({
            streak_push_enabled: s.data.streak_push_enabled,
            streak_push_time: s.data.streak_push_time,
          });
          setStreakLastSent(s.data.streak_push_last_sent_at);
        }
        if (!d && !s) toast.error("Couldn't load settings");
      })
      .finally(() => setLoading(false));
  }, []);

  const save = async (partial) => {
    setSaving(true);
    const next = { ...prefs, ...partial };
    try {
      await api.put("/settings/digest", next);
      setPrefs(next);
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setSending(true);
    try {
      await api.post("/settings/digest/send-test");
      toast.success(`Sent! Check ${email}`);
      setLastSent(new Date().toISOString());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const saveStreak = async (partial) => {
    setStreakSaving(true);
    const next = { ...streakPrefs, ...partial };
    try {
      await api.put("/settings/streak-push", next);
      setStreakPrefs(next);
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setStreakSaving(false);
    }
  };

  const sendStreakTest = async () => {
    setStreakSending(true);
    try {
      await api.post("/settings/streak-push/send-test");
      toast.success("Streak push sent — check your notifications");
      setStreakLastSent(new Date().toISOString());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setStreakSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 md:px-8 py-10 md:py-14" data-testid="settings-page">
        <header className="mb-8">
          <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068]">
            Your preferences
          </div>
          <h1 className="font-heading text-4xl tracking-tighter font-medium text-[#2A2F2B] mt-1">
            Settings
          </h1>
        </header>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-[#6B7068]">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading...
          </div>
        ) : (
          <div className="space-y-6">
            <section
              className="rounded-2xl bg-white border border-[#EAE6E1] p-6"
              data-testid="settings-digest-card"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#D18E6315] flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5 text-[#D18E63]" strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-heading text-xl text-[#2A2F2B]">Daily digest email</h2>
                  <p className="text-sm text-[#6B7068] mt-1">
                    One curated morning brief — movers, what changed in your watchlist, open Wheel positions, and alerts fired in the last 24 hours.
                  </p>
                  <div className="mt-5 space-y-4">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="digest-toggle" className="text-sm text-[#2A2F2B] cursor-pointer">
                        Send me a daily digest
                      </Label>
                      <Switch
                        id="digest-toggle"
                        data-testid="settings-digest-toggle"
                        checked={prefs.digest_enabled}
                        disabled={saving}
                        onCheckedChange={(v) => save({ digest_enabled: v })}
                      />
                    </div>
                    {prefs.digest_enabled && (
                      <div className="flex items-center justify-between gap-3">
                        <Label className="text-sm text-[#2A2F2B] shrink-0">Deliver at (UTC)</Label>
                        <select
                          data-testid="settings-digest-time"
                          value={prefs.digest_time}
                          disabled={saving}
                          onChange={(e) => save({ digest_time: e.target.value })}
                          className="rounded-lg border border-[#EAE6E1] bg-white px-3 py-2 text-sm text-[#2A2F2B] focus:border-[#547A8F] focus:outline-none"
                        >
                          {TIMES.map((t) => (
                            <option key={t} value={t}>
                              {t} UTC
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-3 border-t border-[#EAE6E1]">
                      <div className="text-xs text-[#6B7068]">
                        Sends to <span className="font-mono text-[#2A2F2B]">{email}</span>
                        {lastSent && (
                          <span className="block mt-0.5">
                            Last sent {new Date(lastSent).toLocaleString()}
                          </span>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={sendTest}
                        disabled={sending}
                        data-testid="settings-digest-send-test"
                      >
                        {sending ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                        ) : (
                          <Send className="w-3.5 h-3.5 mr-1.5" />
                        )}
                        Send test now
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section
              className="rounded-2xl bg-white border border-[#EAE6E1] p-6"
              data-testid="settings-streak-push-card"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#C66B5915] flex items-center justify-center shrink-0">
                  <Flame className="w-5 h-5 text-[#C66B59]" strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-heading text-xl text-[#2A2F2B]">Streak nudges</h2>
                  <p className="text-sm text-[#6B7068] mt-1">
                    A single push notification at your chosen time if you haven't done a flashcard yet — keeps your streak alive. Skipped automatically on days you've already practiced.
                  </p>
                  <div className="mt-5 space-y-4">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="streak-push-toggle" className="text-sm text-[#2A2F2B] cursor-pointer">
                        Remind me before I lose my streak
                      </Label>
                      <Switch
                        id="streak-push-toggle"
                        data-testid="settings-streak-push-toggle"
                        checked={streakPrefs.streak_push_enabled}
                        disabled={streakSaving}
                        onCheckedChange={(v) => saveStreak({ streak_push_enabled: v })}
                      />
                    </div>
                    {streakPrefs.streak_push_enabled && (
                      <div className="flex items-center justify-between gap-3">
                        <Label className="text-sm text-[#2A2F2B] shrink-0">Remind me at (UTC)</Label>
                        <select
                          data-testid="settings-streak-push-time"
                          value={streakPrefs.streak_push_time}
                          disabled={streakSaving}
                          onChange={(e) => saveStreak({ streak_push_time: e.target.value })}
                          className="rounded-lg border border-[#EAE6E1] bg-white px-3 py-2 text-sm text-[#2A2F2B] focus:border-[#547A8F] focus:outline-none"
                        >
                          {TIMES.map((t) => (
                            <option key={t} value={t}>
                              {t} UTC
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-3 border-t border-[#EAE6E1]">
                      <div className="text-xs text-[#6B7068]">
                        Delivered via {`browser/iOS/Android push`}
                        {streakLastSent && (
                          <span className="block mt-0.5">
                            Last fired {new Date(streakLastSent).toLocaleString()}
                          </span>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={sendStreakTest}
                        disabled={streakSending}
                        data-testid="settings-streak-push-send-test"
                      >
                        {streakSending ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                        ) : (
                          <Send className="w-3.5 h-3.5 mr-1.5" />
                        )}
                        Test push
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="rounded-2xl bg-white border border-[#EAE6E1] p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#547A8F15] flex items-center justify-center shrink-0">
                  <Bell className="w-5 h-5 text-[#547A8F]" strokeWidth={1.75} />
                </div>
                <div className="flex-1">
                  <h2 className="font-heading text-xl text-[#2A2F2B]">Alert notifications</h2>
                  <p className="text-sm text-[#6B7068] mt-1">
                    Price, RSI, 1-min breakout, and sentiment-shift alerts already fire via web push (works even when the tab is closed). Sentiment-shift alerts <span className="text-[#2A2F2B] font-semibold">also send an email</span> so you don't miss them.
                  </p>
                  <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#5C8D73] font-semibold">
                    <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.25} /> Email + push wired up
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
